import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Loader } from 'lucide-react';
import { StrictMode, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Toaster } from 'sonner';

// Import environment variables
import './i18n/setup';

// Import the generated route tree
import { routeTree } from './routeTree.gen';

import { initApi } from '@/api';
import { AuthProvider, useAuth } from '@/components/features/auth-provider';
import { ThemeProvider } from '@/components/theme-provider';
import '@/lib/env';
import { Logger } from '@/lib/logger';
import { useTranslation } from 'react-i18next';
import { Error } from './components/ui/error';
import { NotFound } from './components/ui/not-found';
import reportWebVitals from './reportWebVitals';
import './styles.css';

Logger.init();

initApi();

export const queryClient = new QueryClient();

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
  defaultErrorComponent: Error,
  defaultOnCatch: (error, errorInfo) => {
    Logger.logErrorWithContext(error, {
      componentStack: errorInfo.componentStack,
      digest: errorInfo.digest,
    });
  },
  context: {
    queryClient,
    isAuthenticated: undefined!,
    persistLogin: undefined!,
    t: undefined!,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const { isAuthenticated, persistLogin } = useAuth();
  const { t } = useTranslation();

  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        isAuthenticated,
        t,
        persistLogin,
      }}
    />
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="app-theme">
        <AuthProvider>
          <Suspense
            fallback={
              <div className="w-full h-full flex items-center justify-center">
                <Loader size="xl" />
              </div>
            }
          >
            <InnerApp />
          </Suspense>
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

// Render the app
const rootElement = document.getElementById('app');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
